import React from 'react'
import { Grid, Theme } from '@material-ui/core'
import LiquidInputAdapter from '@src/components/LiquidInputAdapter'
import { Field } from 'formik'
import { FormattedMessage, useIntl } from 'react-intl'
import { makeStyles } from '@material-ui/core/styles'
import { useDebounce } from 'react-use'
import clsx from 'clsx'
import {
  convertToUppercase,
  baseLengthValue,
  useRunAfterUpdate,
  isBiTubeLightCyclerProduct,
  useProductFromQueryParam,
} from '../ProductsConfiguration'
import ValidationErrors from '@src/routes/Configurators/validations/ValidationErrors'
import { splitMessages } from '../utils'
import ValidationWarnings from '@src/routes/Configurators/validations/ValidationWarnings'
import useTubeValidation from '../useTubeValidation'
import useFormikHelper from '../useFormikHelper'
import { ModificationTypes } from '../enum'
import { Modification, TubeProperties } from '../../../utils/StandardMTOEnum'

interface SequenceProps {
  label: string
  infoText: React.ReactNode
  setLastCursorPosition: React.Dispatch<React.SetStateAction<number>>
  isAntiSense?: boolean
  additionalLabel?: string
  convertTBasesToUBases?: boolean
  disabled?: boolean
  autoGenerateSequence?: (e: string, b: string, c?: boolean) => void
}
const useStyle = makeStyles((theme: Theme) => {
  return {
    infoText: {
      fontSize: theme.typography.pxToRem(12),
    },
    infoError: {
      color: theme.palette.error.main,
      fontSize: theme.typography.pxToRem(14),
    },
    multipleErrorMessage: {
      color: theme.palette.error.main,
      marginTop: theme.spacing(1),
    },
  }
})

const Sequence: React.FC<SequenceProps> = ({
  label,
  infoText,
  setLastCursorPosition,
  isAntiSense,
  additionalLabel,
  convertTBasesToUBases = false,
  disabled = false,
  autoGenerateSequence,
}) => {
  const classes = useStyle()
  const intl = useIntl()
  const formik = useFormikHelper()
  const { validationAPI } = useTubeValidation()
  const runAfterUpdate = useRunAfterUpdate()
  const product = useProductFromQueryParam()
  const biTubeLightCyclerProduct = isBiTubeLightCyclerProduct(product)
  const sequenceType = isAntiSense
    ? TubeProperties.AntiSenseSequence
    : TubeProperties.Sequence
  //TODO: Needs further addition based on the modifications
  const baseCount = () => {
    const sequenceLength = baseLengthValue(
      formik.selectedOligoValues?.[sequenceType] || ''
    )
    return (!formik.errors?.[sequenceType] && sequenceLength) || '--'
  }

  useDebounce(
    async () => {
      if (
        formik.selectedOligoValues[sequenceType] !== '' &&
        !(formik.selectedOligoValues?.autoGenerateOligo && !isAntiSense)
      ) {
        await validationAPI(formik.validationFields)
      } else if (
        formik.touched?.[sequenceType] &&
        formik.selectedOligoValues[sequenceType] === ''
      ) {
        formik.setFieldError(
          sequenceType,
          intl.formatMessage({
            id: 'CONF_REQUIRED_FIELD',
            defaultMessage: 'Required Field',
          })
        )
      }
      // update autogenerate if enabled
      formik.selectedOligoValues?.autoGenerateOligo &&
        !isAntiSense &&
        autoGenerateSequence &&
        autoGenerateSequence(
          formik.selectedOligoValues.sequence,
          formik.selectedOligoValues?.customerOverhang || ''
        )
    },
    500,
    [formik.selectedOligoValues[sequenceType], formik.activeOligoIndex]
  )

  // TODO: Needs further addition based on the requirement
  const formatSequence = (text: string): string => {
    const sequence = convertToUppercase(text)
    return convertTBasesToUBases ? sequence.replace('T', 'U') : sequence
  }

  const handleEmptySequence = () => {
    if (sequenceType === TubeProperties.AntiSenseSequence) {
      formik.setOligoFieldValue(ModificationTypes.AntisenceFiveMod, '', false)
      formik.setOligoFieldValue(ModificationTypes.AntisenceThreeMod, '', false)
      formik.setOligoFieldValue('antisenseBaseCount', undefined, false)
      formik.setOligoFieldValue('antisenseMolecularWeight', '', false)
      formik.setOligoFieldValue('antisenseSecondaryStructure', '', false)
      formik.setOligoFieldValue('antisensePrimerDimer', '', false)
      formik.setOligoFieldValue('antisenseMeltingTemperature', '', false)
      formik.setOligoFieldValue('antisenseGcContent', undefined, false)

      formik.setFieldError(ModificationTypes.AntisenceFiveMod, '')
      formik.setFieldError(ModificationTypes.AntisenceThreeMod, '')
    } else {
      formik.setOligoFieldValue(ModificationTypes.FiveMod, '', false)
      formik.setOligoFieldValue(ModificationTypes.ThreeMod, '', false)
      formik.setOligoFieldValue('fiveModCategory', '', false)
      formik.setOligoFieldValue('threeModCategory', '', false)
      formik.setOligoFieldValue('baseCount', undefined, false)
      formik.setOligoFieldValue('molecularWeight', '', false)
      formik.setOligoFieldValue('secondaryStructure', '', false)
      formik.setOligoFieldValue('primerDimer', '', false)
      formik.setOligoFieldValue('meltingTemperature', '', false)
      formik.setOligoFieldValue('gcContent', undefined, false)
      formik.setOligoFieldValue('sequenceWarning', [], false)
      formik.setOligoFieldValue('reviewWarning', [], false)
      formik.setOligoFieldValue('five-reporter-three-quencher', '', false)

      formik.setFieldError(ModificationTypes.FiveMod, '')
      formik.setFieldError(ModificationTypes.ThreeMod, '')
    }
  }

  const onSequenceChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const input = e.target
    const sequenceString = formatSequence(e.target.value)
    formik.setOligoFieldValue('insertAsterisk', false, false)
    formik.setFieldTouched(sequenceType, true, false)
    formik.setOligoFieldValue(sequenceType, sequenceString, false)
    const curser = input.selectionStart
    setLastCursorPosition(curser)

    const hasInternalMod = ['dualquench', 'internalquench'].includes(
      formik.selectedOligoValues.probeType || ''
    )
    const regex = /\[BHQ[^\]]*\]/g
    const { ThreeMod, FiveMod, InternalMod } = ModificationTypes
    if (
      hasInternalMod &&
      formik.selectedOligoValues.internalMod &&
      (!regex.test(sequenceString) ||
        !sequenceString.includes(formik.selectedOligoValues.internalMod) ||
        baseLengthValue(formik.selectedOligoValues?.[sequenceType] || '') < 10)
    ) {
      formik.setOligoFieldValue('five-reporter-three-quencher', '', false)
      formik.setOligoFieldValue(FiveMod, '', false)
      formik.setOligoFieldValue(ThreeMod, '', false)
      formik.setOligoFieldValue(InternalMod, '', false)
    }
    if (biTubeLightCyclerProduct) {
      formik.setOligoFieldValue(Modification.ThreeMod, '[LCFLC]', false)
    }
    //function to position the curser
    runAfterUpdate(() => {
      input.selectionStart = curser
      input.selectionEnd = curser
    })
    if (sequenceString === '') {
      handleEmptySequence()
    }
  }

  const preventDefault = (e: React.ChangeEvent<HTMLInputElement>) =>
    e.preventDefault()

  const onClickAction = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLastCursorPosition(e.target.selectionStart)
  }
  let errorList
  if (sequenceType === TubeProperties.Sequence) {
    errorList =
      (formik.touched?.sequence &&
        formik.errors?.sequence &&
        splitMessages(formik.errors.sequence)) ||
      []
  } else if (sequenceType === TubeProperties.AntiSenseSequence) {
    errorList =
      (formik.touched?.antisenceSequence &&
        formik.errors?.antisenceSequence &&
        splitMessages(formik.errors.antisenceSequence)) ||
      []
  }
  const warningMessages = formik.selectedOligoValues.sequenceWarning || []

  return (
    <Grid container>
      <Grid item xs={12}>
        <Field
          fieldId={`oligo_${sequenceType}_id`}
          name={formik.oligoFieldName(sequenceType)}
          label={label}
          component={LiquidInputAdapter}
          required
          displayError={false}
          onChange={onSequenceChange}
          onBlur={preventDefault}
          multiline
          onClick={onClickAction}
          value={formik.selectedOligoValues[sequenceType] || ''}
          additionalLabel={additionalLabel}
          additionalLabelStyle={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
          disabled={disabled}
        />
      </Grid>
      <Grid container>
        <Grid item xs={10}>
          <Grid
            container
            className={clsx(classes.infoText, {
              [classes.infoError]: errorList.length > 0,
            })}
          >
            {infoText}
          </Grid>
          {errorList.length === 1 && (
            <ValidationErrors errors={errorList} configuratorsRedesign />
          )}
          {errorList.length > 1 && (
            <>
              <Grid className={classes.multipleErrorMessage}>
                <FormattedMessage
                  id="CONF_ERRORS_IN_INPUT"
                  defaultMessage="Error(s) found in input"
                />
                :
              </Grid>
              <ValidationErrors errors={errorList} />
            </>
          )}
        </Grid>
        <Grid item xs={2}>
          <Grid
            container
            justifyContent="flex-end"
            className={clsx(classes.infoText, {
              [classes.infoError]: errorList.length > 0,
            })}
          >
            <FormattedMessage
              id="CONF_BASE_COUNT"
              defaultMessage="Base Count:"
            />{' '}
            {baseCount()}
          </Grid>
        </Grid>
        <Grid item xs={12}>
          {warningMessages.length > 0 && (
            <Grid container>
              <ValidationWarnings warnings={warningMessages} />
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  )
}

export default Sequence
